/**
 * FindingsInsights
 *
 * Renders the aggregated insights bar (severity distribution, status counts,
 * meta counts) above the BPA Findings data grid.
 */

import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';

import { bpaInsightsAtom } from '../../state';

const FindingsInsights: React.FC = () => {
  // Purely presentational: the fetch that populates bpaInsightsAtom is
  // orchestrated at the page level (see BPAFindingsListing), so this component
  // continues to render even if it is unmounted/remounted (e.g. collapsed).
  const insightsData = useAtomValue(bpaInsightsAtom);
  const insights = insightsData?.insights;
  const isLoading = insightsData?.loading;

  // Extract stats from insights or use zero values when loading to show placeholder UI
  const stats = {
    // Severity counts
    critical: isLoading ? 0 : insights?.critical || 0,
    high: isLoading ? 0 : insights?.high || 0,
    medium: isLoading ? 0 : insights?.medium || 0,
    low: isLoading ? 0 : insights?.low || 0,
    // Status-count keys mirror the normalized BPA status values returned by
    // the backend. Keep them in sync with Finding choices, not raw T12 names.
    Active: isLoading ? 0 : insights?.Active || 0,
    Fixed: isLoading ? 0 : insights?.Fixed || 0,
    Unignored: isLoading ? 0 : insights?.Unignored || 0,
    Ignored: isLoading ? 0 : insights?.Ignored || 0,
    assigned_to_me: isLoading ? 0 : insights?.assigned_to_me || 0,
    watching_by_me: isLoading ? 0 : insights?.watching_by_me || 0,
    unread_notes: isLoading ? 0 : insights?.unread_notes || 0,
    // Meta counts
    unique_controls: isLoading ? 0 : insights?.unique_controls || 0,
    unique_integrations: isLoading ? 0 : insights?.unique_integrations || 0,
    unique_benchmarks: isLoading ? 0 : insights?.unique_benchmarks || 0,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Severity Distribution */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Severity:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'critical',
                  value: stats.critical,
                  label: 'Critical',
                },
                {
                  color: 'serious',
                  value: stats.high,
                  label: 'High',
                },
                {
                  color: 'medium',
                  value: stats.medium,
                  label: 'Medium',
                },
                {
                  color: 'minimal',
                  value: stats.low,
                  label: 'Low',
                },
              ]}
              labelDisplay="value"
              tooltip={`Critical: ${stats.critical}, High: ${stats.high}, Medium: ${stats.medium}, Low: ${stats.low}`}
            />
          </div>

          {/* Count Summaries */}
          {stats.Active > 0 && (
            <InsightItem label="Active" value={stats.Active} />
          )}
          {stats.Fixed > 0 && <InsightItem label="Fixed" value={stats.Fixed} />}
          {stats.Ignored > 0 && (
            <InsightItem label="Ignored" value={stats.Ignored} />
          )}
          {stats.assigned_to_me > 0 && (
            <InsightItem label="Assigned" value={stats.assigned_to_me} />
          )}
          {stats.watching_by_me > 0 && (
            <InsightItem label="Watching" value={stats.watching_by_me} />
          )}
          {stats.unread_notes > 0 && (
            <InsightItem label="Unread Notes" value={stats.unread_notes} />
          )}

          <InsightItem
            label="Integrations"
            value={stats.unique_integrations}
            tooltip="Unique cloud integrations"
          />
          <InsightItem
            label="Benchmarks"
            value={stats.unique_benchmarks}
            tooltip="Unique benchmarks assessed"
            isLast={true}
          />
          <InsightItem
            label="Controls"
            value={stats.unique_controls}
            tooltip="Unique controls assessed"
          />
        </div>
      </Card>
    </div>
  );
};

export default FindingsInsights;
